import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useConnection } from '../App';
import { getPlatformData } from '../actions/adminActions';
import { useOptionalActivityContext } from '../components/Routes/Workspace/Cluster/ActivityContext';
import { IState } from '../reducers';
import { IClusterReducer } from '../reducers/clustersReducer';
import { IContextReducer } from '../reducers/contextReducer';
import { selectSignalREnabled } from './platformConfiguration';
import { useOrganization } from './organizationHooks';
import {
    clusterIdFromLocation,
    organizationUrlNameFromPath,
    SIGNAL_R_RESOURCE_TYPE,
    signalRRoomMembership
} from './signalRRoomMembership';
import { useSignalRResourceRoom } from './useSignalRResourceRoom';

type GetPlatformData = ReturnType<typeof getPlatformData>;

export const SignalRRoomSubscriptions: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const connection = useConnection()?.connection;
    const activityContext = useOptionalActivityContext();
    const { isLoggedIn, signalREnabled, currentChannelId } = useSelector<IState, IContextReducer & IClusterReducer & { signalREnabled: boolean }>(state => ({
        ...state.context,
        ...state.clusters,
        signalREnabled: selectSignalREnabled(state)
    }));
    const organizationUrlName = organizationUrlNameFromPath(location.pathname);
    const { currentOrganization } = useOrganization(organizationUrlName || '');
    const [platformId, setPlatformId] = useState<string>(undefined);
    const activeConnection = isLoggedIn && signalREnabled ? connection : null;
    const clusterId = clusterIdFromLocation(location.pathname, location.search);

    useEffect(() => {
        if (!isLoggedIn || !signalREnabled) {
            setPlatformId(undefined);
            return undefined;
        }

        let cancelled = false;
        dispatch<GetPlatformData>(getPlatformData()).then(response => {
            if (!cancelled && typeof response === 'string' && response.length > 0) {
                setPlatformId(response);
            }
        }).catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [dispatch, isLoggedIn, signalREnabled]);

    useEffect(() => {
        if (!activeConnection) {
            return undefined;
        }

        const rejoin = () => {
            signalRRoomMembership.rejoin(activeConnection);
        };
        activeConnection.onreconnected(rejoin);
        return undefined;
    }, [activeConnection]);

    useSignalRResourceRoom(activeConnection, platformId ? {
        kind: 'personalized',
        resourceType: SIGNAL_R_RESOURCE_TYPE.Platform,
        resourceId: platformId
    } : null);

    useSignalRResourceRoom(activeConnection, currentOrganization?.id ? {
        kind: 'resource',
        resourceType: SIGNAL_R_RESOURCE_TYPE.Organization,
        resourceId: currentOrganization.id
    } : null);

    useSignalRResourceRoom(activeConnection, clusterId ? {
        kind: 'resource',
        resourceType: SIGNAL_R_RESOURCE_TYPE.Cluster,
        resourceId: clusterId
    } : null);

    useSignalRResourceRoom(activeConnection, clusterId && activityContext?.currentChannelId ? {
        kind: 'resource',
        resourceType: SIGNAL_R_RESOURCE_TYPE.TeamChannel,
        resourceId: activityContext.currentChannelId
    } : null);

    useSignalRResourceRoom(activeConnection, clusterId && currentChannelId ? {
        kind: 'resource',
        resourceType: SIGNAL_R_RESOURCE_TYPE.TeamChannel,
        resourceId: currentChannelId
    } : null);

    return null;
};
